"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  FaSearch,
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaCalendarAlt,
  FaExclamationCircle,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaRupeeSign,
  FaSpinner,
} from "react-icons/fa"
import { LuEye } from "react-icons/lu"
import { Link } from "react-router-dom"
import { Pagination } from "../../../utils/pagination.utils"
import { ExcelDownload } from "../../../utils/excel.utils"
import { useAuth } from "../../../provider/auth.provider"
import { formatDate } from "../../activities/edit"
import { SelectWithSearch2, type SelectOption } from "../../../utils/select-with-search"
import {
  FiSearch,
  FiFilter,
  FiX,
  FiCalendar,
  FiRefreshCw,
  FiUser,
  FiCheckSquare,
  FiEdit
} from "react-icons/fi"
import axiosInstance from "../../../libs/axios"
import { toast } from "react-hot-toast"
import { useDebouncedCallback } from "../../../hooks/useDebounce"

interface ProjectClaim {
  id: number
  engineer_id: number
  project_site_id: number
  site_id: string
  site_name: string
  site_district: string
  project_name: string
  activity: string
  survey_date: string
  work_status?: string
  travel_mode?: string
  submitted_site_distance?: number
  submitted_travel_amount?: number
  submitted_da_amount?: number
  submitted_stay_amount?: number
  submitted_stationery_amount?: number
  submitted_other_amount1?: number
  submitted_other_description1?: string
  submitted_other_amount2?: number
  submitted_other_description2?: string
  submitted_engineer_total?: number
  approved_site_distance?: number | string | null
  approved_travel_amount?: number
  approved_da_amount?: number
  approved_stay_amount?: number
  approved_stationery_amount?: number
  approved_other_amount1?: number
  approved_other_amount2?: number
  approved_other_description1?: string | null
  approved_other_description2?: string | null
  approved_extra_amount?: number
  approved_incentive_amount?: number
  final_approved_total?: number
  ta_slab_category?: string
  kms_slab_category?: string
  district_same_as_site?: boolean
  engg_within_state?: boolean
  final_shortest_distance?: number | string | null
  site_distance_from_reporting?: number | string | null
  site_distance_from_attendance?: number | string | null
  terrain_type?: string
  association_type: string
  status: string
  submitted_at?: string
  approved_at?: string
  rejected_at?: string | null
  approved_by?: number
  rejection_reason?: string
  bill_attachments?: string[]
  created_at: string
  updated_at: string
  project_site: {
    id: number
    site_id: string
    site_name: string
    site_engineer_work_date?: string
    created_at: string
    project?: {
      id: number
      project_name?: string
      project_short_name?: string
    }
    state_circle?: {
      id: number
      circle_name?: string
      state_name?: string
    } | null
  }
  engineer?: {
    id: number
    first_name: string
    last_name: string
    association_type?: string
    employee_code?: string | null
  }
  approver?: {
    id: number
    first_name: string
    last_name: string
  } | null
}

interface PaginationType {
  total_count: number
  page: number
  limit: number
  totalPages: number
}

export interface ProjectClaimFilters {
  search?: string
  engineer_id?: string
  engineer_name?: string
  status?: string
  project_id?: string
  site_id?: string
  state_id?: string
  district_id?: string
  min_submitted_amount?: string
  max_submitted_amount?: string
  min_approved_amount?: string
  max_approved_amount?: string
  work_date_from?: string
  work_date_to?: string
  submitted_at_from?: string
  submitted_at_to?: string
  approved_by?: string
  approved_by_name?: string
  is_approved?: string
  is_rejected?: string
  has_attachments?: string
  sortBy?: string
  sortOrder?: string
  association_type?: string
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: ProjectClaimFilters
  onFiltersChange: (filters: ProjectClaimFilters) => void
  onApply: () => void
  onReset: () => void
}

const ProjectClaimFilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset
}) => {
  const [activeTab, setActiveTab] = useState('basic')
  const [selectedApprover, setSelectedApprover] = useState<SelectOption | null>(
    filters.approved_by
      ? {
          id: Number(filters.approved_by),
          value: Number(filters.approved_by),
          label: filters.approved_by_name || `Approver ID: ${filters.approved_by}`
        }
      : null
  )
  const [selectedEngineer, setSelectedEngineer] = useState<SelectOption | null>(
    filters.engineer_id
      ? {
          id: Number(filters.engineer_id),
          value: Number(filters.engineer_id),
          label: filters.engineer_name || `Engineer ID: ${filters.engineer_id}`
        }
      : null
  )

  useEffect(() => {
    if (filters.approved_by) {
      setSelectedApprover({
        id: Number(filters.approved_by),
        value: Number(filters.approved_by),
        label: filters.approved_by_name || `Approver ID: ${filters.approved_by}`
      })
    } else {
      setSelectedApprover(null)
    }
  }, [filters.approved_by, filters.approved_by_name])

  useEffect(() => {
    if (filters.engineer_id) {
      setSelectedEngineer({
        id: Number(filters.engineer_id),
        value: Number(filters.engineer_id),
        label: filters.engineer_name || `Engineer ID: ${filters.engineer_id}`
      })
    } else {
      setSelectedEngineer(null)
    }
  }, [filters.engineer_id, filters.engineer_name])

  if (!isOpen) return null

  const handleInputChange = (field: keyof ProjectClaimFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined
    })
  }

  const handleApproverSelect = (option: SelectOption | null) => {
    setSelectedApprover(option)
    onFiltersChange({
      ...filters,
      approved_by: option ? String(option.value) : undefined,
      approved_by_name: option ? option.label : undefined
    })
  }

  const handleEngineerSelect = (option: SelectOption | null) => {
    setSelectedEngineer(option)
    onFiltersChange({
      ...filters,
      engineer_id: option ? String(option.value) : undefined,
      engineer_name: option ? option.label : undefined
    })
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiSearch },
    // { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'amount', label: 'Amounts', icon: FaRupeeSign },
    { id: 'dates', label: 'Dates', icon: FiCalendar },
    { id: 'approval', label: 'Approval', icon: FiCheckSquare },
    { id: 'other', label: 'Other', icon: FiUser }
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search claims..."
                  value={filters.search || ''}
                  onChange={(e) => handleInputChange('search', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Project ID"
                  value={filters.project_id || ''}
                  onChange={(e) => handleInputChange('project_id', e.target.value)}
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Site ID"
                  value={filters.site_id || ''}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Association Type</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.association_type || ''}
                  onChange={(e) => handleInputChange('association_type', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="inhouse">Inhouse</option>
                  <option value="vendor">Vendor</option>
                </select>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {/* {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="State ID"
                  value={filters.state_id || ''}
                  onChange={(e) => handleInputChange('state_id', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">District ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="District ID"
                  value={filters.district_id || ''}
                  onChange={(e) => handleInputChange('district_id', e.target.value)}
                />
              </div>
            </div>
          )} */}

          {/* Amounts Tab */}
          {activeTab === 'amount' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Submitted Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Min Submitted Amount"
                  value={filters.min_submitted_amount || ''}
                  onChange={(e) => handleInputChange('min_submitted_amount', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Submitted Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Max Submitted Amount"
                  value={filters.max_submitted_amount || ''}
                  onChange={(e) => handleInputChange('max_submitted_amount', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Approved Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Min Approved Amount"
                  value={filters.min_approved_amount || ''}
                  onChange={(e) => handleInputChange('min_approved_amount', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Approved Amount</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors"
                  placeholder="Max Approved Amount"
                  value={filters.max_approved_amount || ''}
                  onChange={(e) => handleInputChange('max_approved_amount', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Survey Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.work_date_from || ''}
                  onChange={(e) => handleInputChange('work_date_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Survey Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.work_date_to || ''}
                  onChange={(e) => handleInputChange('work_date_to', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Submitted Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.submitted_at_from || ''}
                  onChange={(e) => handleInputChange('submitted_at_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Submitted Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.submitted_at_to || ''}
                  onChange={(e) => handleInputChange('submitted_at_to', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Approval Tab */}
          {activeTab === 'approval' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approved By</label>
                <SelectWithSearch2
                  placeholder="Search approver"
                  value={selectedApprover}
                  onChange={handleApproverSelect}
                  apiEndpoint="/users/list"
                  mapOption={(user: any): SelectOption => ({
                    id: user.id,
                    value: user.id,
                    label:
                      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email ||
                      `ID ${user.id}`
                  })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is Approved</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.is_approved || ''}
                  onChange={(e) => handleInputChange('is_approved', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is Rejected</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.is_rejected || ''}
                  onChange={(e) => handleInputChange('is_rejected', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div> */}
            </div>
          )}

          {/* Other Tab */}
          {activeTab === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Engineer</label>
                <SelectWithSearch2
                  placeholder="Search engineer"
                  value={selectedEngineer}
                  onChange={handleEngineerSelect}
                  apiEndpoint="/users/list"
                  mapOption={(user: any): SelectOption => ({
                    id: user.id,
                    value: user.id,
                    label:
                      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email ||
                      `ID ${user.id}`
                  })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Attachments</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_attachments || ''}
                  onChange={(e) => handleInputChange('has_attachments', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply()
                onClose()
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ProjectClaimsListProps {
  claims: ProjectClaim[]
  pagination: PaginationType
  onSearch: (search: string) => void
  onFilterChange: (filter: string) => void
  statusFilter: string
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdvancedFilter?: (filters: ProjectClaimFilters) => void
  loading?: boolean
  sortConfig?: {
    field: string
    direction: "ASC" | "DESC"
  }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
}

export const ProjectClaimsList: React.FC<ProjectClaimsListProps> = ({
  claims,
  pagination,
  onSearch,
  onFilterChange,
  statusFilter,
  onPageChange,
  onLimitChange,
  onAdvancedFilter,
  loading = false,
  sortConfig,
  onSort,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [filters, setFilters] = useState<ProjectClaimFilters>({})
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)
  const { hasPermission } = useAuth()

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    onFilterChange(value)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
  }

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key as keyof ProjectClaimFilters]).length
  }

  const formatCurrency = (amount?: number) => {
    if (!amount) return "₹0"
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        icon: FaClock,
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
        label: "Pending",
      },
      submitted: {
        icon: FaClock,
        bgColor: "bg-blue-100",
        textColor: "text-blue-800",
        label: "Submitted",
      },
      approved: {
        icon: FaCheckCircle,
        bgColor: "bg-green-100",
        textColor: "text-green-800",
        label: "Approved",
      },
      rejected: {
        icon: FaTimesCircle,
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        label: "Rejected",
      },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || {
      icon: FaExclamationCircle,
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
      label: status,
    }

    const IconComponent = config.icon

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        <IconComponent className="w-2 h-2" />
        {config.label}
      </span>
    )
  }

  const getAssociationBadge = (type?: string) => {
    if (!type) {
      return (
        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800"></span>
      )
    }

    return type === "inhouse" ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
        Inhouse
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
        Vendor
      </span>
    )
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction =
      sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />
    }
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-600" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-600" />
    )
  }

  const formatDateValue = (value?: string | null) => {
    return value ? formatDate(value) : "N/A"
  }

  const formatNumber = (value?: number | string | null) => {
    if (value === null || value === undefined || value === "") return ""
    const parsed = typeof value === "string" ? Number(value) : value
    if (Number.isNaN(parsed)) {
      return ""
    }
    return parsed
  }

  const formatBoolean = (value?: boolean | null) => {
    if (value === null || value === undefined) return "N/A"
    return value ? "Yes" : "No"
  }

  const buildEngineerName = (claim: ProjectClaim) => {
    if (!claim.engineer) return "N/A"
    return `${claim.engineer.first_name || ""} ${claim.engineer.last_name || ""}`.trim() || "N/A"
  }

  const getProjectName = (claim: ProjectClaim) => {
    return claim.project_site?.project?.project_name || claim.project_name || "-"
  }

  const getCircleName = (claim: ProjectClaim) => {
    return claim.project_site?.state_circle?.circle_name || "-"
  }

  const getApproverName = (claim: ProjectClaim) => {
    if (!claim.approver) return "-"
    const { first_name = "", last_name = "" } = claim.approver
    const fullName = `${first_name} ${last_name}`.trim()
    return fullName || "-"
  }

  const mapClaimForExport = (claim: ProjectClaim) => {
    const attachmentCount = claim.bill_attachments?.length ?? 0
    
    // Helper function to safely format distance values
    const formatDistance = (value?: number | string | null) => {
      if (value === null || value === undefined || value === "") return ""
      const parsed = typeof value === "string" ? Number(value) : value
      if (Number.isNaN(parsed)) {
        return ""
      }
      return parsed
    }
    
    return {
      "Site ID": claim.project_site?.site_id || "N/A",
      "Site Name": claim.project_site?.site_name || "N/A",
      District: claim.site_district || "N/A",
      "Project Name": getProjectName(claim),
      "Circle Name": getCircleName(claim),
      Activity: claim.activity || "N/A",
      "Survey Date": claim.project_site?.site_engineer_work_date ? formatDate(claim.project_site?.site_engineer_work_date) : "N/A",
      "Travel Mode": claim.travel_mode || "N/A",
      "Submitted Slab Category": claim.ta_slab_category || "",
      "Approved Slab Category": claim.kms_slab_category || "",
      "Submitted Travel KMs": formatDistance(claim.submitted_site_distance),
      "Approved Travel KMs": formatDistance(claim.approved_site_distance),
      "Distance from Reporting Lat/Long": formatDistance(claim.site_distance_from_reporting),
      "Distance from Attendance Lat/Long": formatDistance(claim.site_distance_from_attendance),
      "Final Shortest Distance (KMs)": formatDistance(claim.final_shortest_distance),
      "Terrain Type": claim.terrain_type || "N/A",
      "District Same As Site": formatBoolean(claim.district_same_as_site),
      "Engineer Name": buildEngineerName(claim),
      "Employee Code": claim.engineer?.employee_code || "N/A",
      "Engineer Association": claim.engineer?.association_type || claim.association_type || "N/A",
      "Submitted Site Distance (KM)": formatNumber(claim.submitted_site_distance),
      "Approved Site Distance (KM)": formatNumber(claim.approved_site_distance),
      "Final Shortest Distance": formatNumber(claim.final_shortest_distance),
      "Engineer Within State": formatBoolean(claim.engg_within_state),
      "Travel Amount (Submitted)": formatNumber(claim.submitted_travel_amount),
      "DA Amount (Submitted)": formatNumber(claim.submitted_da_amount),
      "Stay Amount (Submitted)": formatNumber(claim.submitted_stay_amount),
      "Stationery Amount (Submitted)": formatNumber(claim.submitted_stationery_amount),
      "Other Amount 1 (Submitted)": formatNumber(claim.submitted_other_amount1),
      "Other Description 1 (Submitted)": claim.submitted_other_description1 || "N/A",
      "Other Amount 2 (Submitted)": formatNumber(claim.submitted_other_amount2),
      "Other Description 2 (Submitted)": claim.submitted_other_description2 || "N/A",
      "Engineer Total (Submitted)": formatNumber(claim.submitted_engineer_total),
      "Travel Amount (Approved)": formatNumber(claim.approved_travel_amount),
      "DA Amount (Approved)": formatNumber(claim.approved_da_amount),
      "Stay Amount (Approved)": formatNumber(claim.approved_stay_amount),
      "Stationery Amount (Approved)": formatNumber(claim.approved_stationery_amount),
      "Other Amount 1 (Approved)": formatNumber(claim.approved_other_amount1),
      "Other Description 1 (Approved)": claim.approved_other_description1 || "N/A",
      "Other Amount 2 (Approved)": formatNumber(claim.approved_other_amount2),
      "Other Description 2 (Approved)": claim.approved_other_description2 || "N/A",
      "Extra Amount (Approved)": formatNumber(claim.approved_extra_amount),
      "Incentive Amount (Approved)": formatNumber(claim.approved_incentive_amount),
      "Final Approved Total": formatNumber(claim.final_approved_total),
      "Attachments Count": attachmentCount,
      "Attachment Links": attachmentCount && claim.bill_attachments ? claim.bill_attachments.join("\n") : "N/A",
      Status: claim.status,
      "Approved By": getApproverName(claim),
      "Submitted At": formatDateValue(claim.submitted_at),
      "Approved At": formatDateValue(claim.approved_at),
      "Rejected At": formatDateValue(claim.rejected_at),
      "Rejection Reason": claim.rejection_reason || "N/A",
    }
  }

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number> = {
      page,
      limit,
    }

    const effectiveSearch = (filters.search ?? searchTerm ?? "").trim()
    if (effectiveSearch) {
      params.search = effectiveSearch
    }

    if (statusFilter) {
      params.status = statusFilter
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value as string | number
      }
    })

    if (sortConfig?.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const fetchExportData = async () => {
    setIsExporting(true)
    try {
      const pageSize = 100
      let currentPage = 1
      let totalPages = 1
      const allClaims: ProjectClaim[] = []

      do {
        const params = buildExportParams(currentPage, pageSize)
        const response = await axiosInstance.get("/claims/project/list", { params })

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to fetch project claims")
        }

        const fetchedClaims: ProjectClaim[] = response.data.data?.claims || []
        allClaims.push(...fetchedClaims)

        const paginationInfo = response.data.data?.pagination
        const apiTotalPages = paginationInfo?.total_pages ?? paginationInfo?.totalPages ?? totalPages
        totalPages = Math.max(apiTotalPages || 1, 1)
        currentPage += 1
      } while (currentPage <= totalPages)

      if (allClaims.length === 0) {
        toast.error("No project claims found for the current filters")
        return
      }

      setExportData(allClaims.map(mapClaimForExport))
      setShowExcelModal(true)
    } catch (error: any) {
      console.error("Error preparing project claims export:", error)
      toast.error(error?.response?.data?.message || "Failed to prepare project claims export")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Project Claims</h2>
            <p className="text-sm text-gray-600 mt-1">Manage and review project site claims</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search claims..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-64"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
            </div>

            <button
              onClick={() => setIsFilterModalOpen(true)}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded transition-colors text-sm font-medium ${getActiveFiltersCount() > 0
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>

            <select
              value={statusFilter}
              onChange={handleFilterChange}
              className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <button
              onClick={fetchExportData}
              disabled={isExporting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {isExporting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <FaFileExcel className="w-4 h-4" />
                  Export
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Claims</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{pagination.total_count}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaCalendarAlt className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Submitted</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {claims.filter(c => c.status === 'submitted').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
              <FaClock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {claims.filter(c => c.status === 'approved').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
              <FaCheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-900 mt-1">
                {claims.filter(c => c.status === 'rejected').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded flex items-center justify-center">
              <FaTimesCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div> */}

      {/* Table Section */}
      <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Site Details
                </th>
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th> */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Survey Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Association
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engineer Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Circle</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Travel Mode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted Slab Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved Slab Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted Travel KMs</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved Travel KMs</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance from Reporting</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance from Attendance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Final Shortest Distance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("submitted_engineer_total")}
                  >
                    <span>Submitted Amount</span>
                    {getSortIcon("submitted_engineer_total")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("final_approved_total")}
                  >
                    <span>Approved Amount</span>
                    {getSortIcon("final_approved_total")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("status")}
                  >
                    <span>Status</span>
                    {getSortIcon("status")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    type="button"
                    className="flex items-center gap-1 uppercase tracking-wider text-xs font-medium text-gray-500"
                    onClick={() => handleColumnSort("submitted_at")}
                  >
                    <span>Submitted At</span>
                    {getSortIcon("submitted_at")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={22} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                      <p className="text-gray-600 text-sm">Loading claims...</p>
                    </div>
                  </td>
                </tr>
              ) : claims.length === 0 ? (
                <tr>
                  <td colSpan={22} className="px-4 py-12">
                    <div className="flex flex-col items-center justify-center">
                      <FaExclamationCircle className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 text-sm">No claims found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                claims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50 transition-colors">
                    {/* Actions */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {hasPermission('project_claim', 'view') && (
                          <Link
                            to={`/claims/project/${claim.id}`}
                            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                            title="View claim"
                          >
                            <LuEye className="w-4 h-4" />
                          </Link>
                        )}
                        {hasPermission('project_claim', 'view') && claim.status === 'submitted' && (
                          <Link
                            to={`/claims/project/${claim.id}?edit=true`}
                            className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded transition-colors"
                            title="Edit submitted claim"
                          >
                            <FiEdit className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </td>

                    {/* Site Details */}
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900">
                          {claim.project_site?.site_name || "-"}
                        </span>
                        <Link to={`/sites/${claim.project_site?.id}`} className="text-xs text-blue-600">
                          {claim.project_site?.site_id || "-"}
                        </Link>
                      </div>
                    </td>

                    {/* Project */}
                    {/* <td className="px-4 py-4">
                      <div className="flex flex-col max-w-[200px]">
                        <span className="text-sm text-gray-900 truncate" title={claim.project_name}>
                          {claim.project_name}
                        </span>
                        <span className="text-xs text-gray-600 truncate" title={claim.activity}>
                          {claim.activity}
                        </span>
                      </div>
                    </td> */}

                    {/* Survey Date */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {formatDate(claim.project_site?.site_engineer_work_date as string)}
                        </span>
                      </div>
                    </td>

                    {/* Employee Code */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{claim.engineer?.employee_code || "-"}</span>
                    </td>

                    {/* Association - Previous Implementation */}
                    {/*
                    <td className="px-4 py-4 whitespace-nowrap">{getAssociationBadge(claim.association_type)}</td>
                    */}

                    {/* Association */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getAssociationBadge(claim.engineer?.association_type)}
                    </td>

                    {/* Engineer Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{buildEngineerName(claim)}</span>
                    </td>

                    {/* Project Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getProjectName(claim)}</span>
                    </td>

                    {/* Circle Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{getCircleName(claim)}</span>
                    </td>

                    {/* Travel Mode */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{claim.travel_mode || "-"}</span>
                    </td>

                    {/* Submitted Slab Category */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{claim.ta_slab_category || "-"}</span>
                    </td>

                    {/* Approved Slab Category */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{claim.kms_slab_category || "-"}</span>
                    </td>

                    {/* Submitted Travel KMs */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {formatNumber(claim.submitted_site_distance) !== "" ? `${formatNumber(claim.submitted_site_distance)} km` : "-"}
                      </span>
                    </td>

                    {/* Approved Travel KMs */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {formatNumber(claim.approved_site_distance) !== "" ? `${formatNumber(claim.approved_site_distance)} km` : "-"}
                      </span>
                    </td>

                    {/* Distance from Reporting Lat/Long */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {formatNumber(claim.site_distance_from_reporting) !== "" ? `${formatNumber(claim.site_distance_from_reporting)} km` : "-"}
                      </span>
                    </td>

                    {/* Distance from Attendance Lat/Long */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {formatNumber(claim.site_distance_from_attendance) !== "" ? `${formatNumber(claim.site_distance_from_attendance)} km` : "-"}
                      </span>
                    </td>

                    {/* Final Shortest Distance */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {formatNumber(claim.final_shortest_distance) !== "" ? `${formatNumber(claim.final_shortest_distance)} km` : "-"}
                      </span>
                    </td>

                    {/* Submitted Amount */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {/* <FaRupeeSign className="w-3 h-3 text-gray-400" /> */}
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(claim.submitted_engineer_total)}
                        </span>
                      </div>
                    </td>

                    {/* Approved Amount */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {claim.final_approved_total ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-green-900">
                            {formatCurrency(claim.final_approved_total)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">N/A</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(claim.status)}</td>

                    {/* Approved By */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{getApproverName(claim)}</span>
                    </td>

                    {/* Submitted At */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {claim.submitted_at ? formatDate(claim.submitted_at) : "-"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination as any} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={Array.isArray(exportData) ? exportData : []}
        filename="project_claims"
        title="Export Project Claims Data"
        description="Download the filtered project claims with complete details"
      />

      <ProjectClaimFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </div>
  )
}
